/**
 * Cases Tab
 *
 * Displays cases related to a specific account/client.
 * Provides case creation with pre-populated account info.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedCaseRecords } from '../../services/mockData/cases';
import { getRecordsForObject } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { CaseModal } from '../modals/CaseModal';
import { Briefcase, Plus, Calendar, User, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ListViewRecord } from '../../types/listView';

const customSuffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const ACCOUNT_FIELDS = [
  'AccountId__c', `AccountRelField${customSuffix}`, 'AccountRelField__c',
  'Client__c', 'accountId', 'ClientRelField__c', 'custom__client',
];

function extractAccountId(r: Record<string, unknown>): string | null {
  const data = (r.data as Record<string, unknown>) ?? r;
  const rel = (r as { relatedClientId?: unknown }).relatedClientId;
  if (rel != null) return String(rel).trim() || null;
  const fields = ACCOUNT_FIELDS;
  for (const f of fields) {
    const v = data[f] ?? (r as Record<string, unknown>)[f];
    if (v == null) continue;
    if (typeof v === 'object' && v !== null && 'relatedObject' in v) {
      const ro = (v as { relatedObject?: { id?: unknown; key?: unknown } }).relatedObject;
      if (ro?.key != null) return String(ro.key).trim();
      if (ro?.id != null) return String(ro.id).trim();
    }
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
}

function toListViewRecord(r: Record<string, unknown>): ListViewRecord {
  const d = (r.data as Record<string, unknown>) ?? r;
  return {
    id: String(r.id ?? d.id ?? ''),
    caseNumber: (d.CaseNumber__c ?? d.caseNumber ?? d.CaseNumber ?? '') as string,
    subject: (d.Subject__c ?? d.subject ?? d.Subject ?? '') as string,
    status: (d.Status__c ?? d.status ?? d.Status ?? '') as string,
    priority: (d.Priority__c ?? d.priority ?? d.Priority ?? '') as string,
    type: (d.Type__c ?? d.type ?? d.Type ?? '') as string,
    accountId: extractAccountId(r) ?? (d.accountId as string),
    accountName: (d.AccountName__c ?? d.accountName ?? d.AccountName ?? '') as string,
    productName: (d.ProductName__c ?? d.productName ?? d.ProductName ?? '') as string,
    owner: (d.Owner__c ?? d.owner ?? d.Owner ?? '') as string,
    createdDate: (d.CreatedDate__c ?? d.createdDate ?? d.CreatedDate ?? '') as string,
    lastModifiedDate: (d.LastModifiedDate__c ?? d.lastModifiedDate ?? d.LastModifiedDate ?? '') as string,
    ...d,
  } as ListViewRecord;
}

export interface CasesTabPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

interface CasesTabProps {
  /** The ID of the account/client */
  accountId: string;
  /** The name of the account for display */
  accountName: string;
  /** Optional callback when navigating to a case */
  onNavigateToCase?: (caseId: string) => void;
  /** Pre-fetched cases (e.g. from useCreditBlockCases). When provided, skips fetch and uses these. */
  cases?: ListViewRecord[];
  /** Loading state when using pre-fetched cases */
  isLoadingCases?: boolean;
  /** When parent traversal/query failed (e.g. Gobodo error). */
  isErrorCases?: boolean;
  /** Optional message for `isErrorCases` */
  casesErrorMessage?: string;
  /** Server-backed pagination (e.g. Credit Block client detail traversal). */
  casesPagination?: CasesTabPagination;
}

export const CasesTab: React.FC<CasesTabProps> = ({
  accountId,
  accountName,
  onNavigateToCase,
  cases: preFetchedCases,
  isLoadingCases,
  isErrorCases,
  casesErrorMessage,
  casesPagination,
}) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<ListViewRecord | null>(null);

  const { data: fetchedCases = [], isLoading } = useQuery({
    queryKey: ['caseRecords', accountId, shouldUseGobodo()],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const records = await getRecordsForObject('Case', { size: 200 });
        return (records as Record<string, unknown>[]).map(toListViewRecord);
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      return persistedCaseRecords.get();
    },
    // When parent supplies cases (e.g. Credit Block uses traversal), don't run this fetch. Also skip when parent is loading so we don't run a non-traversal request.
    enabled: !preFetchedCases && !isLoadingCases && !isErrorCases,
  });

  const allCases = preFetchedCases ?? fetchedCases;

  const accountCases = useMemo(() => {
    if (preFetchedCases) return preFetchedCases;
    const norm = (s: string | undefined) => (s ?? '').trim();
    const strip = (s: string) => s.replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
    const match = (aid: string | undefined, clientId: string) => {
      const a = norm(aid);
      const b = norm(clientId);
      return a && b && (a === b || strip(a) === strip(b));
    };
    return allCases.filter((c) => match(c.accountId, accountId));
  }, [allCases, accountId, preFetchedCases]);

  // Sort by status (open first), then by priority, then by date
  const sortedCases = useMemo(() => {
    const statusOrder: Record<string, number> = {
      'New': 0,
      'In Progress': 1,
      'Escalated': 2,
      'On Hold': 3,
      'Closed': 4
    };

    const priorityOrder: Record<string, number> = {
      'Critical': 0,
      'High': 1,
      'Medium': 2,
      'Low': 3
    };

    return [...accountCases].sort((a, b) => {
      // Sort by status first
      const statusA = statusOrder[a.status as string] ?? 5;
      const statusB = statusOrder[b.status as string] ?? 5;
      if (statusA !== statusB) return statusA - statusB;

      // Then by priority
      const prioA = priorityOrder[a.priority as string] ?? 2;
      const prioB = priorityOrder[b.priority as string] ?? 2;
      if (prioA !== prioB) return prioA - prioB;

      // Then by created date (newest first)
      const dateA = a.createdDate ? new Date(a.createdDate as string).getTime() : 0;
      const dateB = b.createdDate ? new Date(b.createdDate as string).getTime() : 0;
      return dateB - dateA;
    });
  }, [accountCases]);

  const handleNewCase = () => {
    setSelectedCase(null);
    setIsModalOpen(true);
  };

  const handleEditCase = (caseItem: ListViewRecord) => {
    setSelectedCase(caseItem);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCase(null);
    if (preFetchedCases) {
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'cases'] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['caseRecords'] });
    }
  };

  // Status badge colors
  const statusColors: Record<string, string> = {
    'New': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'In Progress': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Escalated': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    'On Hold': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
    'Closed': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
  };

  // Priority badge colors
  const priorityColors: Record<string, string> = {
    'Critical': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    'High': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    'Medium': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Low': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const totalPages =
    casesPagination && casesPagination.pageSize > 0
      ? Math.max(1, Math.ceil(casesPagination.totalCount / casesPagination.pageSize))
      : 1;
  const displayCaseCount = casesPagination?.totalCount ?? sortedCases.length;

  const loading = preFetchedCases != null ? (isLoadingCases ?? false) : isLoading;
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (isErrorCases) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-6">
        <div className="flex gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
          <div>
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Couldn&apos;t load cases</h3>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-200/90">
              {casesErrorMessage?.trim() ||
                'Case traversal failed. Check your connection and Gobodo configuration, then try again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Cases ({displayCaseCount})
        </h3>
        <button
          onClick={handleNewCase}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Case
        </button>
      </div>

      {sortedCases.length === 0 ? (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No cases yet
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Create cases to track support requests and issues for {accountName}.
            </p>
            <button
              onClick={handleNewCase}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Case
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedCases.map(caseItem => (
            <div
              key={caseItem.id}
              onClick={() => handleEditCase(caseItem)}
              className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-500 dark:hover:border-blue-500 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      {caseItem.caseNumber as string}
                    </span>
                    {(caseItem.priority === 'Critical' || caseItem.escalated) && (
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                  </div>
                  <h4 className={`font-medium truncate ${
                    caseItem.status === 'Closed'
                      ? 'text-gray-500 dark:text-gray-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {caseItem.subject as string}
                  </h4>

                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(caseItem.createdDate as string)}
                    </span>
                    {caseItem.owner && (
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {caseItem.owner as string}
                      </span>
                    )}
                    {caseItem.type && (
                      <span className="text-gray-500">
                        {caseItem.type as string}
                      </span>
                    )}
                    {caseItem.subType && String(caseItem.subType).trim() && (
                      <span className="text-gray-500">
                        {caseItem.subType as string}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    priorityColors[caseItem.priority as string] || priorityColors.Medium
                  }`}>
                    {caseItem.priority as string}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    statusColors[caseItem.status as string] || statusColors.New
                  }`}>
                    {caseItem.status as string}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {casesPagination && casesPagination.totalCount > 0 && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Page {casesPagination.page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={casesPagination.page <= 1}
              onClick={() => casesPagination.onPageChange(casesPagination.page - 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              type="button"
              disabled={casesPagination.page >= totalPages}
              onClick={() => casesPagination.onPageChange(casesPagination.page + 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Case Modal */}
      <CaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        caseRecord={selectedCase ? {
          ...selectedCase
        } : {
          // New case with pre-populated account
          id: '',
          accountId: accountId,
          accountName: accountName
        } as ListViewRecord}
      />
    </div>
  );
};
